import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, getAuthHeaders } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  User,
  Lock,
  CreditCard,
  MessageSquare,
  History,
  Trash2,
  Plus,
  Loader2,
  Check,
  X,
  Ban,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Camera,
  Globe,
  Star,
  Share2,
  QrCode,
  Download,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import QRCode from "qrcode";

// Social media icons (custom for TikTok, X, Pinterest)
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.93 1.42-6 1.42-6s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.03 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.01-.28 1.2.6 2.17 1.78 2.17 2.14 0 3.78-2.25 3.78-5.5 0-2.88-2.07-4.89-5.02-4.89-3.42 0-5.43 2.57-5.43 5.22 0 1.03.4 2.14.9 2.74.1.12.11.22.08.34-.09.38-.3 1.2-.34 1.37-.05.22-.18.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.4 2.97 7.4 6.93 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.35-.63-2.74-1.38l-.75 2.84c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserDashboard = () => {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  
  // PIN
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  
  // Cards
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  
  // Blocked users
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);
  
  // Message history
  const [messageHistory, setMessageHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchMessages, setSearchMessages] = useState("");
  
  // Payment links
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [linkAmount, setLinkAmount] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);
  
  // Languages
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("es");
  const [savingLanguage, setSavingLanguage] = useState(false);
  
  // Profile photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Share profile
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  
  // Favorites
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [userToRemoveFavorite, setUserToRemoveFavorite] = useState(null);
  
  // Social Links
  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    tiktok: "",
    twitter: "",
    instagram: "",
    linkedin: "",
    pinterest: "",
  });
  const [savingSocial, setSavingSocial] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchProfile();
    fetchLanguages();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === "cards") {
      fetchCards();
    } else if (activeTab === "blocked") {
      fetchBlockedUsers();
    } else if (activeTab === "history") {
      fetchMessageHistory();
    } else if (activeTab === "favorites") {
      fetchFavorites();
    } else if (activeTab === "social") {
      fetchSocialLinks();
    }
  }, [activeTab]);

  // Helper: build the shareable URL from any available source
  const getShareUrl = () => {
    const nick = profile?.nickname || user?.nickname || "";
    return profile?.profile_url || (nick ? `${window.location.origin}/u/${nick}` : null);
  };

  // Generate QR code when profile data arrives
  useEffect(() => {
    const target = getShareUrl();
    if (target) {
      QRCode.toDataURL(target, {
        width: 288,
        margin: 2,
        color: { dark: '#e6342a', light: '#ffffff' }
      }).then(setQrCodeUrl).catch(console.error);
    }
  }, [profile?.profile_url, profile?.nickname, user?.nickname]); // eslint-disable-line

  // Also generate QR when the share dialog opens (in case profile wasn't ready yet)
  useEffect(() => {
    if (!showShareDialog) return;
    const target = getShareUrl();
    if (target) {
      QRCode.toDataURL(target, {
        width: 288,
        margin: 2,
        color: { dark: '#e6342a', light: '#ffffff' }
      }).then(setQrCodeUrl).catch(console.error);
    }
  }, [showShareDialog]); // eslint-disable-line

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/user/profile`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setSelectedLanguage(data.language || "es");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await fetch(`${API}/languages`);
      if (response.ok) {
        const data = await response.json();
        setLanguages(data);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const fetchCards = async () => {
    setLoadingCards(true);
    try {
      const response = await fetch(`${API}/user/cards`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCards(data);
      }
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchBlockedUsers = async () => {
    setLoadingBlocked(true);
    try {
      const response = await fetch(`${API}/user/blocked`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data);
      }
    } catch (error) {
      console.error("Error fetching blocked users:", error);
    } finally {
      setLoadingBlocked(false);
    }
  };

  const fetchMessageHistory = async (page = 1, search = "") => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "50" });
      if (search) params.append("search", search);
      
      const response = await fetch(`${API}/user/messages/full-history?${params}`, { 
        credentials: "include",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMessageHistory(data.messages);
        setHistoryTotal(data.total);
        setHistoryPage(page);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchFavorites = async () => {
    setLoadingFavorites(true);
    try {
      const response = await fetch(`${API}/user/favorites`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await fetch(`${API}/user/social-links`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSocialLinks({
          facebook: data.facebook || "",
          tiktok: data.tiktok || "",
          twitter: data.twitter || "",
          instagram: data.instagram || "",
          linkedin: data.linkedin || "",
          pinterest: data.pinterest || "",
        });
      }
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  };

  const handleSaveSocialLinks = async () => {
    setSavingSocial(true);
    try {
      const response = await fetch(`${API}/user/social-links`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(socialLinks),
      });
      
      if (response.ok) {
        toast.success("Redes sociales actualizadas");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSavingSocial(false);
    }
  };

  const fetchPaymentLinks = async () => {
    try {
      const response = await fetch(`${API}/user/payment-links`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPaymentLinks(data);
      }
    } catch (error) {
      console.error("Error fetching payment links:", error);
    }
  };

  const handleSavePin = async () => {
    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      toast.error("El PIN debe ser de 6 dígitos numéricos");
      return;
    }
    
    if (newPin !== confirmPin) {
      toast.error("Los PINs no coinciden");
      return;
    }
    
    setSavingPin(true);
    try {
      const response = await fetch(`${API}/user/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pin: newPin }),
      });
      
      if (response.ok) {
        toast.success("PIN actualizado correctamente");
        setNewPin("");
        setConfirmPin("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al actualizar PIN");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSavingPin(false);
    }
  };

  const handleSaveLanguage = async (langCode) => {
    setSavingLanguage(true);
    try {
      const response = await fetch(`${API}/user/language`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ language: langCode }),
      });
      
      if (response.ok) {
        setSelectedLanguage(langCode);
        toast.success("Idioma actualizado");
      } else {
        toast.error("Error al actualizar idioma");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSavingLanguage(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Use JPG, PNG, GIF o WebP");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es muy grande. Máximo 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API}/user/picture`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, picture: data.picture }));
        // Update auth context
        if (user) {
          login({ ...user, picture: data.picture });
        }
        toast.success("Foto de perfil actualizada");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al subir foto");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!userToUnblock) return;
    
    try {
      const response = await fetch(`${API}/user/unblock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blocked_user_id: userToUnblock.user_id }),
      });
      
      if (response.ok) {
        toast.success("Usuario desbloqueado");
        setBlockedUsers(prev => prev.filter(u => u.user_id !== userToUnblock.user_id));
      }
    } catch (error) {
      toast.error("Error al desbloquear usuario");
    } finally {
      setUserToUnblock(null);
    }
  };

  const handleRemoveFavorite = async () => {
    if (!userToRemoveFavorite) return;
    
    try {
      const response = await fetch(`${API}/user/favorites/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userToRemoveFavorite.user_id }),
      });
      
      if (response.ok) {
        toast.success("Eliminado de favoritos");
        setFavorites(prev => prev.filter(u => u.user_id !== userToRemoveFavorite.user_id));
      }
    } catch (error) {
      toast.error("Error al eliminar favorito");
    } finally {
      setUserToRemoveFavorite(null);
    }
  };

  const handleDeleteCard = async () => {
    if (!cardToDelete) return;
    
    try {
      const response = await fetch(`${API}/user/cards/${cardToDelete.payment_method_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Tarjeta eliminada");
        setCards(prev => prev.filter(c => c.payment_method_id !== cardToDelete.payment_method_id));
      }
    } catch (error) {
      toast.error("Error al eliminar tarjeta");
    } finally {
      setCardToDelete(null);
    }
  };

  const handleCreatePaymentLink = async () => {
    if (!linkAmount || parseFloat(linkAmount) < 1) {
      toast.error("El monto mínimo es $1.00");
      return;
    }
    
    setCreatingLink(true);
    try {
      const response = await fetch(`${API}/user/payment-links/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(linkAmount),
          currency: "mxn",
          description: linkDescription || `Pago a ${user?.name}`,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Link de pago creado");
        setPaymentLinks(prev => [data, ...prev]);
        setShowCreateLink(false);
        setLinkAmount("");
        setLinkDescription("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear link");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setCreatingLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const downloadQR = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `okchat-qr-${profile?.nickname || 'profile'}.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f17] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f17] text-white">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
      />

      {/* Header */}
      <header className="border-b border-slate-800 bg-[#1a1a24]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/chat")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-semibold">Mi Perfil</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShareDialog(true)}
              className="border-red-600 text-red-400 hover:bg-red-600/20"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartir
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1a1a24] border border-slate-800 p-1 grid grid-cols-7 w-full">
            <TabsTrigger value="profile" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <User className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Perfil</span>
            </TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <Share2 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Redes</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <Lock className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Seguridad</span>
            </TabsTrigger>
            <TabsTrigger value="favorites" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <Star className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Favoritos</span>
            </TabsTrigger>
            <TabsTrigger value="cards" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <CreditCard className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Tarjetas</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <History className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Historial</span>
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              <Ban className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Bloqueados</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#1a1a24] border-slate-800">
                <CardHeader>
                  <CardTitle>Información del Perfil</CardTitle>
                  <CardDescription>Tu información básica de cuenta</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex items-center gap-4">
                    <div className="relative group">
                      <Avatar className="w-24 h-24">
                        <AvatarImage src={profile?.picture ? `${BACKEND_URL}${profile.picture}` : null} />
                        <AvatarFallback className="bg-gradient-to-br from-red-500 to-indigo-600 text-white text-3xl">
                          {profile?.name?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {uploadingPhoto ? (
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        ) : (
                          <Camera className="w-6 h-6 text-white" />
                        )}
                      </button>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{profile?.name}</h3>
                      <p className="text-slate-400">@{profile?.nickname}</p>
                      <p className="text-xs text-slate-500 mt-1">ID: {profile?.user_id}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-400">{profile?.stats?.total_messages || 0}</p>
                      <p className="text-xs text-slate-400">Mensajes</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{profile?.stats?.conversations_count || 0}</p>
                      <p className="text-xs text-slate-400">Chats</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-400">{profile?.stats?.favorites_count || 0}</p>
                      <p className="text-xs text-slate-400">Favoritos</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Email</p>
                    <p className="text-slate-200">{profile?.email}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Miembro desde</p>
                    <p className="text-slate-200">
                      {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Language Settings */}
              <Card className="bg-[#1a1a24] border-slate-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Idioma de la Aplicación
                  </CardTitle>
                  <CardDescription>Selecciona tu idioma preferido</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedLanguage}
                    onValueChange={handleSaveLanguage}
                    disabled={savingLanguage}
                  >
                    <SelectTrigger className="w-full bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Selecciona un idioma" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code} className="text-white hover:bg-slate-700">
                          <span className="flex items-center gap-2">
                            <span>{lang.native}</span>
                            <span className="text-slate-400 text-xs">({lang.name})</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {savingLanguage && (
                    <p className="text-sm text-slate-400 mt-2 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Guardando...
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Social Links Tab */}
          <TabsContent value="social">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Redes Sociales
                </CardTitle>
                <CardDescription>Añade tus perfiles de redes sociales para que otros puedan encontrarte</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Facebook */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <Facebook className="w-4 h-4 text-blue-500" />
                      Facebook
                    </label>
                    <Input
                      placeholder="https://facebook.com/tu-perfil"
                      value={socialLinks.facebook}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  {/* Instagram */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <Instagram className="w-4 h-4 text-pink-500" />
                      Instagram
                    </label>
                    <Input
                      placeholder="https://instagram.com/tu-usuario"
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  {/* TikTok */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <TikTokIcon />
                      TikTok
                    </label>
                    <Input
                      placeholder="https://tiktok.com/@tu-usuario"
                      value={socialLinks.tiktok}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, tiktok: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  {/* X (Twitter) */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <XIcon />
                      X (Twitter)
                    </label>
                    <Input
                      placeholder="https://x.com/tu-usuario"
                      value={socialLinks.twitter}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, twitter: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  {/* LinkedIn */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <Linkedin className="w-4 h-4 text-blue-600" />
                      LinkedIn
                    </label>
                    <Input
                      placeholder="https://linkedin.com/in/tu-perfil"
                      value={socialLinks.linkedin}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedin: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  {/* Pinterest */}
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400 flex items-center gap-2">
                      <PinterestIcon />
                      Pinterest
                    </label>
                    <Input
                      placeholder="https://pinterest.com/tu-usuario"
                      value={socialLinks.pinterest}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, pinterest: e.target.value }))}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-700">
                  <Button
                    onClick={handleSaveSocialLinks}
                    disabled={savingSocial}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {savingSocial ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      "Guardar Redes Sociales"
                    )}
                  </Button>
                </div>
                
                <p className="text-sm text-slate-500">
                  Estos enlaces serán visibles en tu perfil público y permitirán a otros usuarios conectar contigo en otras plataformas.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader>
                <CardTitle>Cambiar PIN de Acceso</CardTitle>
                <CardDescription>Tu PIN de 6 dígitos protege tu cuenta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <label className="text-sm text-slate-400">Nuevo PIN</label>
                  <Input
                    type="password"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Ingresa 6 dígitos"
                    className="bg-slate-800 border-slate-700 text-center tracking-[0.5em] font-mono"
                    maxLength={6}
                    inputMode="numeric"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-slate-400">Confirmar PIN</label>
                  <Input
                    type="password"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Confirma tu PIN"
                    className="bg-slate-800 border-slate-700 text-center tracking-[0.5em] font-mono"
                    maxLength={6}
                    inputMode="numeric"
                  />
                </div>
                <Button
                  onClick={handleSavePin}
                  disabled={savingPin || newPin.length !== 6 || newPin !== confirmPin}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {savingPin ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Actualizar PIN
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  Usuarios Favoritos
                </CardTitle>
                <CardDescription>Tus contactos favoritos para acceso rápido</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingFavorites ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                  </div>
                ) : favorites.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tienes usuarios favoritos</p>
                    <p className="text-sm mt-2">Marca usuarios como favoritos desde el chat</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {favorites.map((fav) => (
                      <div key={fav.user_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={fav.picture ? `${BACKEND_URL}${fav.picture}` : null} />
                              <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                                {fav.name?.charAt(0)?.toUpperCase() || "?"}
                              </AvatarFallback>
                            </Avatar>
                            {fav.status === 'online' && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#1a1a24]" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-1">
                              {fav.name}
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            </p>
                            <p className="text-sm text-slate-400">@{fav.nickname}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setUserToRemoveFavorite(fav)}
                          className="text-slate-400 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cards Tab */}
          <TabsContent value="cards">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Tarjetas Guardadas</CardTitle>
                  <CardDescription>Tus métodos de pago para transacciones rápidas</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {loadingCards ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                  </div>
                ) : cards.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tienes tarjetas guardadas</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cards.map((card) => (
                      <div key={card.payment_method_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <CreditCard className="w-8 h-8 text-red-400" />
                          <div>
                            <p className="font-medium capitalize">{card.brand} •••• {card.last4}</p>
                            <p className="text-sm text-slate-400">Exp: {card.exp_month}/{card.exp_year}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {card.is_default && (
                            <span className="text-xs bg-red-600 px-2 py-1 rounded">Por defecto</span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setCardToDelete(card)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Links Section */}
            <Card className="bg-[#1a1a24] border-slate-800 mt-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Links de Pago</CardTitle>
                  <CardDescription>Crea links para recibir pagos</CardDescription>
                </div>
                <Button onClick={() => setShowCreateLink(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Crear Link
                </Button>
              </CardHeader>
              <CardContent>
                {paymentLinks.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <LinkIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tienes links de pago creados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentLinks.map((link, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="font-medium">${link.amount} {link.currency?.toUpperCase()}</p>
                          <p className="text-sm text-slate-400">{link.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyToClipboard(link.url)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(link.url, '_blank')}
                            className="text-slate-400 hover:text-white"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader>
                <CardTitle>Historial Completo de Chats</CardTitle>
                <CardDescription>Todos los mensajes enviados y recibidos en tus conversaciones</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Buscar mensajes..."
                    value={searchMessages}
                    onChange={(e) => setSearchMessages(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchMessageHistory(1, searchMessages)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                  </div>
                ) : messageHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay mensajes</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-2">
                      {messageHistory.map((msg) => (
                        <div 
                          key={msg.message_id} 
                          className={`p-3 rounded-lg ${
                            msg.is_own_message 
                              ? 'bg-red-600/10 border border-red-600/20' 
                              : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                msg.is_own_message 
                                  ? 'bg-red-600/20 text-red-400' 
                                  : 'bg-blue-600/20 text-blue-400'
                              }`}>
                                {msg.is_own_message ? 'Enviado' : 'Recibido'}
                              </span>
                              <span className="text-sm text-slate-400">
                                {msg.is_own_message ? 'Tú' : msg.sender_name || msg.sender_nickname}
                              </span>
                              <span className="text-xs text-slate-500">en {msg.conversation_name}</span>
                            </div>
                            <span className="text-xs text-slate-500">
                              {new Date(msg.created_at).toLocaleString('es-MX')}
                            </span>
                          </div>
                          <p className="text-slate-200">
                            {msg.type === 'image' ? '📷 Imagen' : 
                             msg.type === 'video' ? '🎥 Video' : 
                             msg.type === 'audio' || msg.type === 'walkie' ? '🎤 Audio' : 
                             msg.type === 'document' ? '📄 Documento' :
                             msg.type === 'gif' ? '🎞️ GIF' :
                             msg.content || '[Mensaje sin contenido]'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                
                {historyTotal > 50 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyPage === 1}
                      onClick={() => fetchMessageHistory(historyPage - 1, searchMessages)}
                      className="border-slate-700"
                    >
                      Anterior
                    </Button>
                    <span className="text-slate-400 py-2">Página {historyPage} de {Math.ceil(historyTotal / 50)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={historyPage * 50 >= historyTotal}
                      onClick={() => fetchMessageHistory(historyPage + 1, searchMessages)}
                      className="border-slate-700"
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blocked Users Tab */}
          <TabsContent value="blocked">
            <Card className="bg-[#1a1a24] border-slate-800">
              <CardHeader>
                <CardTitle>Usuarios Bloqueados</CardTitle>
                <CardDescription>Los usuarios bloqueados no pueden enviarte mensajes</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingBlocked ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                  </div>
                ) : blockedUsers.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Ban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No has bloqueado a ningún usuario</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blockedUsers.map((blockedUser) => (
                      <div key={blockedUser.user_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={blockedUser.picture ? `${BACKEND_URL}${blockedUser.picture}` : null} />
                            <AvatarFallback className="bg-gradient-to-br from-red-500 to-orange-600 text-white">
                              {blockedUser.name?.charAt(0)?.toUpperCase() || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{blockedUser.name}</p>
                            <p className="text-sm text-slate-400">@{blockedUser.nickname}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUserToUnblock(blockedUser)}
                          className="border-slate-700 text-slate-300 hover:bg-slate-700"
                        >
                          Desbloquear
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Share Profile Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-[#0f0f1a] border-slate-700/50 text-white sm:max-w-lg p-0 overflow-hidden">
          <div className="bg-gradient-to-br from-red-600/20 to-transparent p-6 pb-4 border-b border-slate-700/50">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Share2 className="w-5 h-5 text-red-400" />
                Compartir Perfil
              </DialogTitle>
              <p className="text-slate-400 text-sm mt-1">Invita a otros a chatear contigo en OkChat</p>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-5">
            <div className="flex gap-5 items-center">
              <div className="flex flex-col items-center shrink-0">
                {qrCodeUrl ? (
                  <div className="bg-white p-3 rounded-2xl shadow-lg shadow-red-500/10">
                    <img src={qrCodeUrl} alt="QR Code" className="w-36 h-36" />
                  </div>
                ) : (
                  <div className="w-36 h-36 bg-slate-800 rounded-2xl flex items-center justify-center border border-slate-700">
                    <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={downloadQR}
                  className="mt-2 text-slate-400 hover:text-white text-xs px-2 h-7"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Descargar QR
                </Button>
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-slate-400 text-xs mb-1">Tu perfil público</p>
                  <p className="text-white font-semibold text-lg">@{profile?.nickname || user?.nickname}</p>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-3 border border-slate-700/50">
                  <p className="text-slate-500 text-xs mb-1">Escanea el QR o comparte el link:</p>
                  <p className="text-red-400 text-xs font-mono break-all">
                    {profile?.profile_url || `${window.location.origin}/u/${profile?.nickname || user?.nickname}`}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(profile?.profile_url || `${window.location.origin}/u/${profile?.nickname || user?.nickname}`)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                >
                  <Copy className="w-3 h-3 mr-1.5" />
                  Copiar enlace
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-700/50" />
              <span className="text-slate-500 text-xs">Compartir en</span>
              <div className="flex-1 h-px bg-slate-700/50" />
            </div>

            {(() => {
              const shareUrl = profile?.profile_url || `${window.location.origin}/u/${profile?.nickname || user?.nickname}`;
              const shareText = `¡Agrégate en OkChat! Mi usuario es @${profile?.nickname || user?.nickname}`;
              const socials = [
                {
                  name: 'WhatsApp',
                  color: 'bg-green-600 hover:bg-green-700',
                  url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.885 3.488"/>
                    </svg>
                  )
                },
                {
                  name: 'Facebook',
                  color: 'bg-blue-600 hover:bg-blue-700',
                  url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
                  icon: <Facebook className="w-4 h-4" />
                },
                {
                  name: 'Twitter/X',
                  color: 'bg-slate-700 hover:bg-slate-600',
                  url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
                  icon: <XIcon />
                },
                {
                  name: 'Telegram',
                  color: 'bg-sky-600 hover:bg-sky-700',
                  url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  )
                },
                {
                  name: 'LinkedIn',
                  color: 'bg-blue-700 hover:bg-blue-800',
                  url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
                  icon: <Linkedin className="w-4 h-4" />
                }
              ];
              return (
                <div className="grid grid-cols-5 gap-2">
                  {socials.map((s) => (
                    <button
                      key={s.name}
                      onClick={() => window.open(s.url, '_blank', 'noopener,noreferrer')}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-white transition-all active:scale-95 ${s.color}`}
                      title={s.name}
                    >
                      {s.icon}
                      <span className="text-[9px] font-medium leading-none">{s.name.split('/')[0]}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Payment Link Dialog */}
      <Dialog open={showCreateLink} onOpenChange={setShowCreateLink}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Crear Link de Pago</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Monto (MXN)</label>
              <Input
                type="number"
                value={linkAmount}
                onChange={(e) => setLinkAmount(e.target.value)}
                placeholder="0.00"
                min="1"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Descripción (opcional)</label>
              <Input
                value={linkDescription}
                onChange={(e) => setLinkDescription(e.target.value)}
                placeholder="Pago por..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <Button
              onClick={handleCreatePaymentLink}
              disabled={creatingLink || !linkAmount}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {creatingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
              Crear Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unblock User Confirmation */}
      <AlertDialog open={!!userToUnblock} onOpenChange={() => setUserToUnblock(null)}>
        <AlertDialogContent className="bg-[#1a1a24] border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desbloquear usuario?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro que deseas desbloquear a {userToUnblock?.name}? Podrá enviarte mensajes de nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleUnblockUser} className="bg-red-600 hover:bg-red-700">
              Desbloquear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Favorite Confirmation */}
      <AlertDialog open={!!userToRemoveFavorite} onOpenChange={() => setUserToRemoveFavorite(null)}>
        <AlertDialogContent className="bg-[#1a1a24] border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar de favoritos?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro que deseas eliminar a {userToRemoveFavorite?.name} de tus favoritos?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveFavorite} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Card Confirmation */}
      <AlertDialog open={!!cardToDelete} onOpenChange={() => setCardToDelete(null)}>
        <AlertDialogContent className="bg-[#1a1a24] border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tarjeta?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro que deseas eliminar esta tarjeta terminada en {cardToDelete?.last4}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCard} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserDashboard;
